/**
 * Die EINE Regel, die aus den Tatsachen einer Anfrage einen Satz macht: wer ist dran, was steht
 * an, und welche EINE Handlung folgt daraus.
 *
 * Bauplan: `docs/KONZEPT-ANFRAGEN-WER-IST-DRAN.md` — § 1 (die Liste wird eine Arbeitsliste, der
 * Stand ist ein ganzer Satz, je Zeile genau eine Aktion), § 2 (der Satz wird SERVERSEITIG
 * gebildet, und: kein Satz ohne Beleg), § 5 (Architektur), **§ 6 (die verbindliche Zuordnung
 * Stand → Gruppe → Aktion, dreizehn Zeilen)**. Kriterien P0.2 und P0.3 in
 * `docs/LOOP-ANFRAGEN-WER-IST-DRAN.md`. Vorbild der Denkweise: `apps/api/src/lib/tasks-board.ts`
 * (`computeAutoTasks` — Rohdaten rein, fertige Zeilen raus, ohne DB).
 *
 * ── Warum ein eigenes Paket und nicht `apps/api/src/lib/anfrage-stand.ts` ─────────────────────
 * Das Kriterium P0.2 nennt den Pfad in `apps/api`. Er bleibt es nicht, und zwar aus einem
 * Grund, der in diesem Repo schon mehrfach Geld gekostet hat: `apps/web` kann nicht aus
 * `apps/api` importieren. Diese Regel liefert nicht nur den Satz, sondern auch die
 * **Gruppen** (§ 1: vier Gruppen statt vier Tabs, mit Überschriften) und die **Aktionen**
 * (Beschriftung + Ziel). Genau das braucht Phase P3 in der Oberfläche. Läge die Regel in
 * `apps/api`, müsste `apps/web` die Gruppen-Überschriften und Aktions-Beschriftungen ein zweites
 * Mal führen — und zwei Listen, die dasselbe behaupten, laufen auseinander. Dieselbe Entscheidung
 * wurde für `@maestro/kundenhistorie-anzeige`, `@maestro/recherche-stand` und
 * `@maestro/anreicherung` schon getroffen; hier wird sie nur nicht noch einmal neu erfunden.
 *
 * Das Paket hat bewusst **kein `test`-Skript**: die Ratsche `apps/api/test/ci-testabdeckung.test.ts`
 * verlangt für jedes Paket MIT `test`-Skript einen eigenen Aufruf in `.github/workflows/test.yml`.
 * Die Tests liegen in `apps/api/test/anfrage-stand.test.ts` — jene Suite läuft in der CI garantiert.
 *
 * ── Rein ─────────────────────────────────────────────────────────────────────────────────────
 * Keine Datenbank, kein Netz, **kein Modell**, kein `new Date()`. Der Bezugszeitpunkt wird
 * übergeben (`fakten.jetzt`) — sonst wäre die Funktion weder reproduzierbar noch prüfbar. Ein
 * Satz über einen Geschäftsvorgang darf nicht halluziniert werden können (§ 7): er wird
 * ZUSAMMENGESETZT, nicht formuliert.
 *
 * ── Kein Satz ohne Beleg (P0.3, die eigentliche Auflage) ─────────────────────────────────────
 * Jeder Teilsatz ist ein `StandBaustein` und nennt die Felder, auf denen er ruht (`belege`).
 * Fehlt die Tatsache, fehlt der Teilsatz — es wird nichts geraten und nichts geschätzt. Damit das
 * nicht bloß eine Absichtserklärung im Kommentar bleibt, prüft der Test mechanisch: für jeden
 * erzeugten Baustein muss `istBelegt()` über JEDES genannte Feld wahr sein (bzw. falsch, wenn der
 * Baustein ausdrücklich eine ABWESENHEIT behauptet — „Die Gästezahl fehlt"). `istBelegt` ist
 * exportiert, damit Regel und Ratsche denselben Begriff von „vorhanden" benutzen und nicht zwei.
 *
 * ── Beschreibend, nie bewertend (§ 6.1 des Recherche-Konzepts, hier genauso) ─────────────────
 * Kein Score, keine Ampel, keine Quote, keine Prognose. Gezählte Vergangenheit in Worten:
 * „dreimal angesehen", „Einmal nachgefasst". `dringlichkeit` ist ausdrücklich KEINE Bewertung des
 * Kunden, sondern die Festlegung aus § 6: **Rot bleibt echten Fehlern** (Zustellung
 * fehlgeschlagen, Anzahlung überfällig) — nicht dem normalen Warten.
 *
 * ── Zeitzone ─────────────────────────────────────────────────────────────────────────────────
 * Alle Datumsgrenzen liegen in **Europe/Berlin** — dieselbe Zone wie jede andere fachliche
 * Datumsgrenze in MAESTRO (`packages/db/sql/85_overdue_and_mail_settings.sql`, N-9). Das ist
 * nicht kosmetisch: ein Angebot, das am 3.8. um 23:30 Uhr UTC rausgeht, ist in Berlin schon am
 * 4.8. verschickt worden. In UTC gerechnet stünde in der Liste ein Tag zu viel Wartezeit.
 * Die zonen-verankerte Werktagsrechnung (`werktageSeit`) kommt aus `@maestro/feiertage` und wird
 * hier nur durchgereicht (seit BUGHUNT-2026-08-15 Q1/Q6 rechnen Anzeige UND Nachfass-Fristen mit
 * derselben Funktion); die übrigen Datumsgrenzen dieser Datei nutzen dieselben Berliner
 * **Tagesanker** (UTC-Mitternacht des Berliner Kalendertages) statt der rohen Zeitstempel.
 */
import { werktageSeit } from "@maestro/feiertage";

/* ────────────────────────────── Eingabe: die Tatsachen ─────────────────────────────────────── */

/** Zone aller fachlichen Datumsgrenzen (sql/85). */
export const ZEITZONE_STANDARD = "Europe/Berlin";

/** Ab wie vielen Werktagen ohne Antwort das Nachfassen ansteht (§ 6, Zeile 7). */
export const NACHFASS_WERKTAGE = 3;

/** Ab wie vielen Nachfassungen ohne jede Öffnung „Als verloren schließen" gilt (§ 6, Zeile 8). */
export const VERLOREN_NACHFASSUNGEN = 2;

/**
 * Wie nah der Wunschtermin sein muss, damit er im Satz erwähnt wird. Weiter entfernte Termine
 * sind wahr, aber keine Information — sie machen jeden Satz länger, ohne etwas zu ändern.
 */
export const TERMIN_HINWEIS_TAGE = 14;

/**
 * Buchungsstrecke D2 (docs/LOOP-BUCHUNGSSTRECKE.md, Konzept Verkaufspsychologie § 4 Punkte 7–8):
 * ab wie vielen Aufrufen der Angebotsseite ohne Zusage ein Vorgang „heiß“ ist. Wer das Angebot
 * dreimal öffnet und nicht zusagt, wägt ab — dann hilft ein Anruf mehr als eine weitere Mail.
 *
 * EINE Schwelle für Board UND Nachfass-Sweep (`apps/api/src/lib/followup-sweep.ts`): liefe sie an
 * zwei Stellen, stünde ein Vorgang oben mit „Jetzt anrufen“ und bekäme trotzdem die nächste Mail.
 */
export const HEISS_AUFRUFE = 3;

/** Was aus `inquiries` selbst kommt. */
export interface AnfrageTatsachen {
  /** `inquiries.id` — nur für das Ziel der Aktion, nie Bestandteil eines Satzes. */
  id: string;
  /** `inquiries.guests`. `null` = keine Angabe. */
  gaeste: number | null;
  /** `inquiries.message`. */
  nachricht: string | null;
  /** `inquiries.event_type` (Migration 133). */
  anlass: string | null;
  /** Wunschtermin (`inquiries.event_date` bzw. `events.event_date`). */
  terminAm: Date | null;
  /**
   * `true` ⇔ `inquiries.status === 'generating'` — der automatische Entwurf (E0.2b,
   * `apps/api/src/lib/inquiry-auto-draft.ts`, ausgelöst über die Queue seit 03.08.2026) hat den
   * Erzeugungs-Anspruch für diese Anfrage gerade geclaimt und arbeitet daran. Zeile 14 (§ 6)
   * übersteuert damit ausdrücklich, was die übrigen Tatsachen sonst nahelegen würden — sonst
   * sagt der Satz "Anlass unbekannt" über eine Anfrage, an der der Automat bereits sitzt.
   *
   * Eigenes benanntes Feld statt eines Zeichenkettenvergleichs auf `stand` an der Prüfstelle:
   * der Rohwert von `stand` ist eine Datenbank-Spalte, kein Vertrag der Regel — ein magischer
   * String `"generating"` mitten in `standAusRegel` wäre die Art Kopplung, die P0.3 vermeiden
   * soll (ein Satz beruft sich auf eine benannte Tatsache, nicht auf eine implizite Konvention).
   */
  generierungLaeuft: boolean;
  /**
   * Buchungsstrecke D2: Telefonnummer des Gastes (`customers.phone`, Rückfall `inquiries.phone`).
   * Nur für das Ziel der Aktion „Jetzt anrufen“ (`tel:`) und für den Hinweis „Keine Nummer
   * hinterlegt“. Optional, damit bestehende Fixtures ohne das Feld „keine Nummer“ bedeuten.
   */
  telefon?: string | null;
  /**
   * Buchungsstrecke D2: als Testanfrage gekennzeichnet (`inquiries.is_test`, Migration 519). Eine
   * Testanfrage ist nie „heiß“ — die Aufrufe stammen vom Betrieb selbst.
   */
  istTest?: boolean;
}

/** Was aus `events`, `offer_options` und `emails` kommt. */
export interface AngebotTatsachen {
  /** `events.id` der erzeugten Veranstaltung — `null`, solange es keine gibt. */
  eventId: string | null;
  /**
   * `offer_options.created_at` der jüngsten Option. `null` ⇔ es gibt gar kein Angebot.
   * Bewusst KEIN eigener „fertiggestellt"-Zeitstempel (§ 4: eine neue Spalte für eine
   * Formulierungsnuance lohnt nicht).
   */
  entwurfSeit: Date | null;
  /**
   * Steht in mindestens einer Variante des Angebots mindestens eine Position (`offer_items`)?
   *
   * Ohne diese Tatsache stützte sich „Angebot fertig" allein auf die EXISTENZ einer Option — und
   * die Prüfung am Prod-Stand (P5.1, 10.08.2026) fand zwei von zehn Vorgängen mit einer leeren.
   * Die Liste bot dort „Angebot senden" an, hinter dem Knopf lag ein leeres Dokument. Ein leeres
   * Angebot ist nicht fertig; die Unterscheidung gehört zu den Tatsachen, nicht in den Wortlaut.
   */
  hatPositionen: boolean;
  /** `events.offer_sent_at`. */
  versendetAm: Date | null;
  /**
   * Zeilen 17/18 (Antoine, 27.09.2026): Zeitpunkt der LETZTEN Nachricht des Betriebs an den Kunden
   * (Freitext ohne Angebot, Rückfrage, Antwort aus dem Posteingang) — aber nur, solange der Kunde
   * danach nicht geantwortet hat. Kommt eine eingehende Nachricht am Vorgang, ist das Feld `null`.
   *
   * Bewusst NICHT `versendetAm`: eine Nachfrage ist kein Angebot (`offer_sent_at` bleibt beim
   * Freitext absichtlich leer, `routes/offers.ts`). Gebildet in `lib/anfragen-abfrage.ts`.
   *
   * Optional nur, damit bestehende Fixtures ohne das Feld weiter „keine Nachfrage" bedeuten;
   * `faktenAusZeile` (apps/api) setzt es immer.
   */
  nachfrageAm?: Date | null;
  /** `emails.status = 'bounced'` zur Angebotsmail. */
  zustellungFehlgeschlagen: boolean;
  /**
   * Buchungsstrecke D2: `events.status = 'offer_sent'` — das Angebot liegt beim Gast und ist noch
   * offen. Benannte Tatsache statt Statusvergleich in der Regel (derselbe Grund wie bei
   * `durchgefuehrt`). Optional: fehlt sie, ist kein Vorgang „heiß“ — lieber eine Anrufaufgabe zu
   * wenig als eine an einem Vorgang, dessen Stand nicht erhoben wurde.
   */
  angebotOffen?: boolean;
  /**
   * `events.offer_view_count` (P1). **`null` heißt „nicht erhoben", nicht „nie geöffnet"** — eine
   * fehlende Messung darf nie als gezählte Null auftreten.
   */
  aufrufe: number | null;
  /**
   * Einblicke (Migration 730): Aufrufe der Angebotsseite SEIT DEM LETZTEN VERSAND
   * (`angebots_aufrufe` mit `versand_nr = events.offer_send_count`). `aufrufe` zählt seit 730 über
   * alle Versände weiter (der Zähler wird beim Neuversand nicht mehr genullt); die Heiß-Regel
   * (Zeile 19) und die Karte meinen aber das AKTUELLE Angebot. Fehlt das Feld (`undefined`, ältere
   * Aufrufer), gilt `aufrufe`; `null` heißt „nicht erhoben" — dann nie heiß.
   */
  aufrufeSeitVersand?: number | null;
  /** `events.offer_last_viewed_at` (P1). */
  zuletztAngesehenAm: Date | null;
  /** `events.followup_stage`. */
  nachfassungen: number | null;
  /** `events.last_followup_at`. */
  zuletztNachgefasstAm: Date | null;
  /** `offer_options.chosen_at` der gewählten Option. */
  angenommenAm: Date | null;
  /** `offer_options.title` der gewählten Option. */
  gewaehlteOption: string | null;
  /** `offer_options.amount_total_cents` — server-autoritativ, nie aus dem Modell (§ 7). */
  betragCents: number | null;
  /** Zeitpunkt der Ablehnung (aus `activity_log`, siehe P4.1). */
  abgelehntAm: Date | null;
  /** Zeitpunkt der Absage durch den Betrieb. */
  abgesagtAm: Date | null;
  /**
   * Zeile 16 (Antoine, 22.09.2026): der Betrieb hat den Vorgang auf **„keine Antwort"** gestellt
   * (`events.status = 'no_response'`) — der Kunde hat sich nicht mehr gemeldet.
   *
   * Warum das eine eigene Tatsache braucht und nicht unter „abgesagt" mitläuft: es ist keine
   * Absage. Niemand hat Nein gesagt; die Sache ist im Sande verlaufen. Der Unterschied steht in
   * jeder Auswertung (verloren ≠ nie beantwortet) und im Satz, den der Betreiber liest.
   *
   * **Wozu es gebraucht wird:** bis hierher kannte diese Regel nur `abgelehntAm`/`abgesagtAm`.
   * Ein auf „keine Antwort" gestellter Vorgang blieb deshalb unveraendert unter „Du bist dran"
   * stehen — der Klick des Betreibers hatte sichtbar keine Wirkung.
   */
  ohneAntwortSeit: Date | null;
  /** Der vom BETREIBER notierte Grund (L3) — nie eine Vermutung des Systems. */
  ablehnungsgrund: string | null;
  /**
   * Zeile 15 (Antoine, 20.09.2026: „Veranstaltungen, die als ‚Durchgeführt‘ gelten, dürfen nicht
   * in der Spalte ‚Du bist dran‘ auftauchen.") — `events.status = 'completed'`.
   *
   * Warum als BENANNTE Tatsache und nicht als Status-Vergleich mitten in der Regel: derselbe
   * Grund wie bei `generierungLaeuft` (Zeile 14) — der Rohwert einer Datenbankspalte ist kein
   * Vertrag dieser Regel. Übersetzt wird er an der Grenze
   * (`apps/api/src/lib/anfrage-tatsachen.ts`); hier steht nur, was er bedeutet.
   */
  durchgefuehrt: boolean;
  /**
   * Zeile 15, zweite Hälfte: der Vorgang ist verbindlich (`events.status` ∈ 'offer_chosen',
   * 'paid'), OHNE dass eine Angebotsvariante die Zusage trägt.
   *
   * Kein Randfall: am 20.09.2026 rein lesend gegen die Produktionsdaten gezählt waren das 10 von
   * 28 Vorgängen im Zustand „zugesagt" — telefonisch oder von Hand abgeschlossene Buchungen, die
   * nie ein Angebot hatten. Sie fielen durch die Zeilen 10/11 (die an `offer_options.chosen_at`
   * hängen) und landeten danach in „kein Angebot …", also unter „Du bist dran" — obwohl längst
   * entschieden war.
   */
  verbindlichGebucht: boolean;
}

/** Was aus `payments` kommt (`payment_type = 'deposit'`). */
export interface AnzahlungTatsachen {
  /** Es gibt einen Anzahlungs-Datensatz bzw. eine Zahlungsaufforderung. */
  angefordert: boolean;
  /** Fälligkeitstag, der in Europe/Berlin bereits vorbei ist. `null` = nicht überfällig. */
  ueberfaelligSeit: Date | null;
}

export interface AnfrageFakten {
  /**
   * Bezugszeitpunkt. **Immer übergeben** — kein `new Date()` in dieser Datei. Ohne übergebenen
   * Zeitpunkt wäre „seit drei Werktagen" weder testbar noch reproduzierbar.
   */
  jetzt: Date;
  /** Für die Feiertage der Werktagsrechnung, z.B. „BY". Unbekannt ⇒ nur bundesweite Feiertage. */
  bundesland: string;
  /** Abweichende Zone nur für Tests; fachlich immer `ZEITZONE_STANDARD`. */
  zeitzone?: string;
  anfrage: AnfrageTatsachen;
  angebot: AngebotTatsachen;
  anzahlung: AnzahlungTatsachen;
}

/* ────────────────────────────── Ausgabe ────────────────────────────────────────────────────── */

/** Die vier Gruppen aus § 1. Die Schlüssel sind Vertrag (P2.2 nennt „du_bist_dran" wörtlich). */
export type StandGruppe = "du_bist_dran" | "kunde_ist_dran" | "gebucht" | "abgelehnt";

/** Überschriften der Gruppen (§ 1). Hier, damit die Oberfläche sie nicht ein zweites Mal führt. */
export const GRUPPEN_TITEL: Record<StandGruppe, string> = {
  du_bist_dran: "Du bist dran",
  kunde_ist_dran: "Kunde ist dran",
  gebucht: "Gebucht",
  abgelehnt: "Abgelehnt oder abgesagt",
};

/** Reihenfolge der Gruppen in der Liste; die beiden letzten stehen eingeklappt (§ 1, Punkt 1). */
export const GRUPPEN_REIHENFOLGE: readonly StandGruppe[] = [
  "du_bist_dran",
  "kunde_ist_dran",
  "gebucht",
  "abgelehnt",
];

/* ── Anzeige-Gruppen: „Entschieden" fasst zwei Regel-Gruppen zusammen (32a, § 12.5) ──────────── */

/**
 * Warum es diese zweite Ebene gibt: 32a zeigt „Gebucht" und „Abgelehnt oder abgesagt" unter EINER
 * Überschrift „Entschieden", mit der Aufschlüsselung im Untertitel. **Zusammengefasst wird nur die
 * Anzeige** — die vier Regel-Gruppen oben bleiben unverändert, denn an ihnen hängt die Zuordnung
 * Stand → Gruppe → Aktion (§ 6) und die Auswertung (`@maestro/auswertung` liest `GRUPPEN_TITEL`).
 *
 * Warum die Wörter trotzdem hier wohnen und nicht in der Oberfläche: derselbe Grund wie bei
 * `GRUPPEN_TITEL` — eine Überschrift, die an zwei Stellen formuliert wird, wird an zwei Stellen
 * geändert, und dann sagen Liste und Auswertung Verschiedenes über dieselbe Menge.
 */
export type AnzeigeGruppe = "du_bist_dran" | "kunde_ist_dran" | "entschieden";

/** Welche Regel-Gruppe unter welcher Überschrift erscheint. */
export const ANZEIGE_GRUPPE_VON: Record<StandGruppe, AnzeigeGruppe> = {
  du_bist_dran: "du_bist_dran",
  kunde_ist_dran: "kunde_ist_dran",
  gebucht: "entschieden",
  abgelehnt: "entschieden",
};

/** Überschriften der Anzeige (§ 12.5). */
export const ANZEIGE_GRUPPEN_TITEL: Record<AnzeigeGruppe, string> = {
  du_bist_dran: "Du bist dran",
  kunde_ist_dran: "Kunde ist dran",
  entschieden: "Entschieden",
};

/** Reihenfolge auf dem Bildschirm; „Entschieden" steht eingeklappt (§ 1, Punkt 1). */
export const ANZEIGE_GRUPPEN_REIHENFOLGE: readonly AnzeigeGruppe[] = [
  "du_bist_dran",
  "kunde_ist_dran",
  "entschieden",
];

/**
 * Die Wörter der Aufschlüsselung im Untertitel von „Entschieden" (§ 12.5, wörtlich:
 * *„1 gewonnen · 5 verloren oder abgesagt"*). „verloren oder abgesagt" trägt beide Fälle, weil die
 * Regel-Gruppe `abgelehnt` beide enthält — die Absage durch den Betrieb ist keine Ablehnung durch
 * den Kunden, aber für die Liste dasselbe Ende.
 */
export const ENTSCHIEDEN_WORT: Record<"gebucht" | "abgelehnt", string> = {
  gebucht: "gewonnen",
  abgelehnt: "verloren oder abgesagt",
};

/**
 * Zusatz im Untertitel — er sagt, was die Gruppe bedeutet, nicht wie viele darin liegen.
 * Nur „Kunde ist dran" bekommt einen (§ 12.5: `4 Anfragen · Angebot liegt beim Kunden`); bei
 * „Du bist dran" sagt die Überschrift schon alles, ein zweiter Satz wäre Füllung.
 */
const ANZEIGE_GRUPPEN_ZUSATZ: Partial<Record<AnzeigeGruppe, string>> = {
  // 27.09.2026: seit den Zeilen 17/18 stehen hier auch Nachfragen ohne Angebot.
  kunde_ist_dran: "Angebot oder Nachfrage liegt beim Kunden",
};

/**
 * Der Untertitel einer Anzeige-Gruppe, zusammengesetzt aus gezählten Zeilen — nicht formuliert.
 *
 * `anzahl` zählt je **Regel**-Gruppe; die Summe der zugehörigen Gruppen ergibt die Zahl vorn.
 * Ein Bestandteil mit `0` fällt weg: „0 gewonnen" ist zwar wahr, aber keine Auskunft — und die
 * Aufschlüsselung soll sagen, was dasteht, nicht was fehlt.
 */
export function gruppenUntertitel(
  gruppe: AnzeigeGruppe,
  anzahl: Readonly<Record<StandGruppe, number>>,
): string {
  let summe = 0;
  for (const g of GRUPPEN_REIHENFOLGE) {
    if (ANZEIGE_GRUPPE_VON[g] === gruppe) summe += anzahl[g];
  }

  const teile = [`${summe} ${summe === 1 ? "Anfrage" : "Anfragen"}`];

  // Aufschlüsselung nur dort, wo die Überschrift zwei Ausgänge zusammenfasst.
  if (gruppe === "entschieden") {
    if (anzahl.gebucht > 0) teile.push(`${anzahl.gebucht} ${ENTSCHIEDEN_WORT.gebucht}`);
    if (anzahl.abgelehnt > 0) teile.push(`${anzahl.abgelehnt} ${ENTSCHIEDEN_WORT.abgelehnt}`);
  }

  const zusatz = ANZEIGE_GRUPPEN_ZUSATZ[gruppe];
  if (zusatz) teile.push(zusatz);

  return teile.join(" · ");
}

/**
 * Rot bleibt echten Fehlern (§ 6, Schlusssatz) — Zustellung fehlgeschlagen, Anzahlung überfällig.
 * Normales Warten ist **nicht** rot, auch wenn es lange dauert.
 */
export type Dringlichkeit = "normal" | "wichtig" | "fehler";

/**
 * Die Aktionen aus § 1, Punkt 3 — „✨ Angebot erstellen" ist die KI-Fassung von Zeile 3.
 * AP6: `stand_festlegen` entfiel mit der Zeile, die sie auslöste (§ 6, ehem. Zeile 6).
 */
export type AktionSchluessel =
  | "angebot_erstellen"
  | "angebot_erstellen_ki"
  | "rueckfrage_schreiben"
  | "angebot_bearbeiten"
  | "angebot_senden"
  | "adresse_korrigieren"
  | "nachfassen"
  | "verloren_schliessen"
  | "anzahlung_anfordern"
  | "veranstaltung_oeffnen"
  | "grund_notieren"
  | "jetzt_anrufen";

/** Beschriftungen — wörtlich aus § 1 Punkt 3 bzw. § 6. */
export const AKTION_TEXT: Record<AktionSchluessel, string> = {
  angebot_erstellen: "Angebot erstellen",
  angebot_erstellen_ki: "✨ Angebot erstellen",
  rueckfrage_schreiben: "✨ Rückfrage schreiben",
  angebot_bearbeiten: "Angebot bearbeiten",
  angebot_senden: "Angebot überprüfen",
  adresse_korrigieren: "Adresse korrigieren",
  nachfassen: "✨ Nachfassen",
  verloren_schliessen: "Als verloren schließen",
  anzahlung_anfordern: "Anzahlung anfordern",
  veranstaltung_oeffnen: "Veranstaltung öffnen",
  grund_notieren: "Grund notieren",
  jetzt_anrufen: "Jetzt anrufen",
};

export interface StandAktion {
  schluessel: AktionSchluessel;
  /** Fertige deutsche Beschriftung. */
  text: string;
  /**
   * Wohin die Zeile navigiert. **Keine Aktion wird nachgebaut** (§ 7): das Ziel ist immer eine
   * Stelle, die es schon gibt (Muster `deepLink` in `lib/tasks-board.ts`).
   */
  ziel: string;
}

/**
 * Ein Teilsatz mit seinem Beleg. Der Kern von P0.3 — jede Behauptung zeigt auf ein Feld.
 */
export interface StandBaustein {
  /** Der Wortlaut, ohne Verbinder. */
  text: string;
  /**
   * Feldpfade in `AnfrageFakten`, z.B. `"angebot.versendetAm"`. **Alle** müssen belegt sein
   * (bzw. alle unbelegt, wenn `belegtAbwesenheit`).
   */
  belege: readonly string[];
  /** `true` = der Teilsatz sagt aus, dass genau diese Felder FEHLEN („Die Gästezahl fehlt"). */
  belegtAbwesenheit?: boolean;
}

/**
 * Die dreizehn Zeilen der Zuordnungstabelle (§ 6), in derselben Reihenfolge. Ein stabiler
 * Schlüssel je Zeile, damit Route, Oberfläche und Test über dieselbe Zeile reden und nicht über
 * einen Satz.
 */
export type StandRegel =
  | "kein_angebot_gaeste_fehlen"
  | "kein_angebot_rueckfrage"
  | "kein_angebot_vollstaendig"
  | "angebot_unversendet"
  | "zustellung_fehlgeschlagen"
  | "nachfassen"
  | "verloren_schliessen"
  | "wartet"
  | "gebucht_anzahlung"
  | "gebucht_erledigt"
  | "abgelehnt"
  | "angebot_leer"
  | "wird_generiert"
  | "verbindlich_ohne_zusage"
  | "ohne_antwort"
  | "nachfrage_wartet"
  | "nachfrage_nachfassen"
  | "heiss_anrufen";

/** Genau die vierzehn Zeilen aus § 6 — der Test fährt sie einzeln ab. */
export const ALLE_REGELN: readonly StandRegel[] = [
  "kein_angebot_gaeste_fehlen",
  "kein_angebot_rueckfrage",
  "kein_angebot_vollstaendig",
  "angebot_unversendet",
  "zustellung_fehlgeschlagen",
  "nachfassen",
  "verloren_schliessen",
  "wartet",
  "gebucht_anzahlung",
  "gebucht_erledigt",
  "abgelehnt",
  // Zeile 13 (P5.1) — bewusst ans Ende und nicht neben Zeile 4 gesetzt: 35 Stellen in Code,
  // Tests und Dokumenten verweisen auf „Zeile N" der Tabelle, eine Einfuegung in der Mitte haette
  // sie alle stillschweigend verschoben. Die PRUEFreihenfolge unten ist davon unabhaengig.
  "angebot_leer",
  // Zeile 14 (19./20.08.2026, Antoine: automatischer Entwurf statt "✨ Rückfrage schreiben") —
  // aus demselben Grund ans Ende gehaengt wie Zeile 13, nicht an ihre fachliche Stelle neben den
  // Zeilen 1-3.
  "wird_generiert",
  // Zeile 15 (20.09.2026, Antoine: „Veranstaltungen, die als ‚Durchgeführt‘ gelten, duerfen nicht
  // in der Spalte ‚Du bist dran‘ auftauchen") — wieder ans Ende gehaengt, aus demselben Grund wie
  // die Zeilen 13 und 14: die Tabellennummern bleiben stabil.
  "verbindlich_ohne_zusage",
  // Zeile 16 (22.09.2026, Antoine: „habe ich gerade gesetzt auf keine Antwort. dann muss sie in
  // der linken Spalte immer verschwinden") — wieder ans Ende, damit die Tabellennummern stabil
  // bleiben.
  "ohne_antwort",
  // Zeilen 17/18 (27.09.2026, Antoine: eine aus MAESTRO gesendete Nachfrage setzt die Karte auf
  // „Kunde ist dran", bis der Kunde antwortet) — ans Ende, damit die Tabellennummern stabil bleiben.
  "nachfrage_wartet",
  "nachfrage_nachfassen",
  // Zeile 19 (Buchungsstrecke D2, 29.09.2026): ≥ HEISS_AUFRUFE Aufrufe ohne Zusage → „Jetzt
  // anrufen“ oben in „Du bist dran“ — ans Ende, damit die Tabellennummern stabil bleiben.
  "heiss_anrufen",
];

export interface AnfrageStand {
  /** Welche Zeile der Tabelle gegriffen hat. */
  regel: StandRegel;
  gruppe: StandGruppe;
  /** Der fertige Satz — zusammengesetzt aus `bausteine`, nie formuliert. */
  satz: string;
  /**
   * Der erste vollständige Satz: **was der Fall ist** (Entwurf 32a, Konzept § 12.3).
   *
   * Er entsteht HIER und nicht in der Oberfläche. Der Grund ist derselbe wie bei Gruppen und
   * Aktionen: wo ein Satz aufhört, ist eine Aussage über den Inhalt — die Anzeige müsste sie sonst
   * aus der Zeichenkette zurückrechnen, und ein deutsches Kurzdatum („24.07.") endet selbst auf
   * einem Punkt. Der `SatzBau` weiß es ohne Raten, weil jeder Baustein seinen Anschluss kennt.
   */
  kopf: string;
  /** Alles nach dem ersten Satz: **woran man es sieht**. Leer, wenn der Stand ein Satz ist. */
  rest: string;
  /**
   * Der kleine Chip über dem Satz (Entwurf 32a, Konzept § 12.4): `Termin in 4 Tagen` ·
   * `Wunschtermin vorbei` · `Nachfassen fällig`. `null` = es gibt keine zeitliche Tatsache.
   *
   * Er ist **keine vierte Dringlichkeitsstufe** neben `normal`/`wichtig`/`fehler`, sondern eine
   * rein zeitliche Auskunft. Er entsteht HIER, weil beide Fristen hier schon liegen
   * (`TERMIN_HINWEIS_TAGE`, `NACHFASS_WERKTAGE`): rechnete die Oberfläche sie nach, gäbe es zwei
   * Rechnungen derselben Frist — und Satz und Chip liefen auseinander.
   */
  chip: string | null;
  /**
   * „24.07." (via `datumKurz`), wenn ein Angebot bereits verschickt wurde
   * (`AnfrageFakten.angebot.versendetAm`), sonst `null`.
   *
   * Getrennt vom `satz`/`kopf`/`rest`: dort steckt dieselbe Tatsache oft im unbetonten `rest`
   * (z. B. wenn seither ein Werktag verstrichen ist und `ohneAntwortTeil` den `kopf` übernimmt,
   * siehe § 6 Zeile 11/12) — für ein schnell scanbares Badge in der Liste reicht das nicht. Die
   * Oberfläche formuliert daraus nichts Neues, sie zeigt nur dasselbe Datum als Chip zusätzlich
   * an (konsistent mit `EVENT_STATUS.offer_sent` in `apps/web/src/ui/format.ts`). Bewusst NICHT
   * aus `regel`/`gruppe` zurückgerechnet: bei `abgelehnt` etwa ist unklar, ob vorher versendet
   * wurde, und bei `wird_generiert` kann bereits ein früheres Angebot verschickt sein, während
   * eine neue Variante entsteht — nur die rohe Tatsache selbst ist hier eindeutig.
   */
  angebotVersendetAm: string | null;
  /**
   * Sortierschlüssel für „Dringendes zuerst" INNERHALB einer Gruppe — aufsteigend, kleiner steht
   * weiter oben. Die Oberfläche ordnet danach und sortiert nicht selbst (§ 12.4).
   */
  sortierung: number;
  /** Genau EINE Aktion, oder keine (§ 6, Zeile 9: „es ist nichts zu tun"). */
  aktion: StandAktion | null;
  dringlichkeit: Dringlichkeit;
  /** Die Teilsätze mit ihren Belegen — Grundlage der Ratsche zu P0.3. */
  bausteine: readonly StandBaustein[];
}

/* ────────────────────────────── „Belegt" — ein Begriff, nicht zwei ─────────────────────────── */

/**
 * Gilt eine Tatsache als vorhanden?
 *
 * Absichtlich streng und an einer Stelle: Regel UND Ratsche benutzen dieselbe Funktion, sonst
 * könnte der Satz „belegt" anders auslegen als seine Prüfung — und die Prüfung wäre wertlos.
 *
 * - `null`/`undefined` ⇒ nicht belegt.
 * - Zeichenkette: nur mit sichtbarem Inhalt („   " ist kein Anlass).
 * - Wahrheitswert: `true` ist die Tatsache, `false` ist ihr Fehlen.
 * - Zahl: **echt größer null**. Eine Zählung von 0 („nie geöffnet") ist KEIN Beleg für eine
 *   Aussage über etwas Vorhandenes — sie belegt eine Abwesenheit, und die zeigt der Satz über den
 *   fehlenden Zeitstempel an.
 * - Datum: nur ein gültiger Zeitpunkt (`Invalid Date` ist kein Datum).
 */
export function istBelegt(wert: unknown): boolean {
  if (wert === null || wert === undefined) return false;
  if (typeof wert === "boolean") return wert;
  if (typeof wert === "number") return Number.isFinite(wert) && wert > 0;
  if (typeof wert === "string") return wert.trim().length > 0;
  if (wert instanceof Date) return Number.isFinite(wert.getTime());
  return true;
}

/** Liest einen Feldpfad wie `"angebot.versendetAm"` aus den Fakten — für die Ratsche. */
export function faktWert(fakten: AnfrageFakten, pfad: string): unknown {
  let aktuell: unknown = fakten;
  for (const teil of pfad.split(".")) {
    if (aktuell === null || typeof aktuell !== "object") return undefined;
    aktuell = (aktuell as Record<string, unknown>)[teil];
  }
  return aktuell;
}

/* ────────────────────────────── Zeit: Berliner Kalendertage ────────────────────────────────── */

interface Kalendertag {
  jahr: number;
  monat: number;
  tag: number;
}

/**
 * Der Kalendertag eines Zeitpunkts in der gewünschten Zone. Über `formatToParts` statt über eine
 * eigene Sommerzeit-Rechnung: die Umstellungsregel steht bereits in der Laufzeit, eine zweite
 * Fassung hier wäre die erste Stelle, an der beide auseinanderlaufen.
 */
function kalendertag(zeitpunkt: Date, zeitzone: string): Kalendertag | null {
  if (!(zeitpunkt instanceof Date) || !Number.isFinite(zeitpunkt.getTime())) return null;
  const teile = new Intl.DateTimeFormat("en-CA", {
    timeZone: zeitzone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(zeitpunkt);
  const lies = (art: string) => Number(teile.find((t) => t.type === art)?.value ?? NaN);
  const jahr = lies("year");
  const monat = lies("month");
  const tag = lies("day");
  if (!Number.isFinite(jahr) || !Number.isFinite(monat) || !Number.isFinite(tag)) return null;
  return { jahr, monat, tag };
}

/**
 * UTC-Mitternacht des Kalendertages in der Zone. Genau diese Ankertage machen aus einer
 * UTC-Tagesdifferenz die BERLINER Tagesdifferenz (siehe `kalendertageBis`).
 */
function tagesAnker(zeitpunkt: Date, zeitzone: string): Date | null {
  const t = kalendertag(zeitpunkt, zeitzone);
  return t ? new Date(Date.UTC(t.jahr, t.monat - 1, t.tag)) : null;
}

/**
 * Volle Werktage zwischen zwei Zeitpunkten, gerechnet über Berliner Kalendertage.
 *
 * **Exportiert seit P8**, weil die Vorgangsansicht (`@maestro/angebots-verlauf`) im Kopf-Satz
 * genau dieselbe Größe nennt („Seit der letzten Nachfassung sind drei Werktage vergangen"). Eine
 * zweite Werktagsrechnung mit eigener Zonen-Verankerung wäre die erste Stelle, an der Liste und
 * Vorgangsansicht dieselbe Wartezeit verschieden angeben.
 *
 * Seit BUGHUNT-2026-08-15 Q1/Q6 lebt die Implementierung in `@maestro/feiertage` und wird hier
 * nur noch durchgereicht — auch die Nachfass-FRISTEN (apps/api/src/lib/followup.ts) rechnen
 * jetzt mit genau dieser Funktion, und Anzeige und Frist können nicht mehr auseinanderlaufen.
 */
export { werktageSeit };

/** Kalendertage von `jetzt` bis `ziel` (negativ = vorbei), in Berliner Tagesgrenzen. */
function kalendertageBis(ziel: Date, jetzt: Date, zeitzone: string): number | null {
  const a = tagesAnker(jetzt, zeitzone);
  const b = tagesAnker(ziel, zeitzone);
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/**
 * „24.07." — Tag und Monat, wie in Antoines Entwurf. Das Jahr steht nur dabei, wenn es ein
 * anderes ist als das laufende: „24.07.2025". Ein Datum ohne Jahr ist über den Jahreswechsel
 * hinweg mehrdeutig, und eine mehrdeutige Angabe ist im Sinne von § 2 keine Tatsache.
 */
export function datumKurz(zeitpunkt: Date, jetzt: Date, zeitzone: string): string | null {
  const t = kalendertag(zeitpunkt, zeitzone);
  if (!t) return null;
  const tt = String(t.tag).padStart(2, "0");
  const mm = String(t.monat).padStart(2, "0");
  const heute = kalendertag(jetzt, zeitzone);
  return heute && heute.jahr !== t.jahr ? `${tt}.${mm}.${t.jahr}` : `${tt}.${mm}.`;
}

/**
 * „24.07. um 14:32" — dasselbe Kurzdatum plus Uhrzeit in derselben Zone.
 *
 * **Neu mit P8**, weil eine Ereigniszeile ohne Zeitpunkt keine Ereigniszeile ist (Kriterium P8.3:
 * „Verlauf als Ereigniszeilen mit Zeit, Person und Beleg"). Ein Kalendertag allein genügt dort
 * nicht: an einem Tag können Versand, Öffnung und Nachfassung liegen, und die Reihenfolge ist die
 * halbe Aussage. Bewusst HIER und nicht in der Vorgangsansicht: Datum und Uhrzeit derselben
 * Software dürfen nicht an zwei Stellen verschieden gebildet werden — `datumKurz` und diese
 * Funktion teilen sich `kalendertag`, also auch die Sommerzeit-Regel der Laufzeit.
 */
export function datumZeitKurz(zeitpunkt: Date, jetzt: Date, zeitzone: string): string | null {
  const datum = datumKurz(zeitpunkt, jetzt, zeitzone);
  if (datum === null) return null;
  try {
    const uhr = new Intl.DateTimeFormat("de-DE", {
      timeZone: zeitzone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(zeitpunkt);
    return `${datum} um ${uhr} Uhr`;
  } catch {
    return datum;
  }
}

/* ────────────────────────────── Sprache ───────────────────────────────────────────────────── */

const ZAHLWORT = [
  "null",
  "ein",
  "zwei",
  "drei",
  "vier",
  "fünf",
  "sechs",
  "sieben",
  "acht",
  "neun",
  "zehn",
  "elf",
  "zwölf",
] as const;

/**
 * „drei" für 3, „13" für 13 — ausgeschrieben bis zwölf, darüber in Ziffern.
 *
 * Exportiert seit P8: die Vorgangsansicht zählt Werktage und Öffnungen in denselben Worten wie
 * die Liste. Zwei Zahlwort-Tabellen wären zwei Schreibweisen derselben Zahl.
 */
export function zahlwort(n: number): string {
  return ZAHLWORT[n] ?? String(n);
}

/** „dreimal", „Einmal" — ab dreizehn in Ziffern, weil ausgeschriebene Zahlen dort stören. */
export function malWort(n: number): string {
  if (n === 1) return "einmal";
  const wort = ZAHLWORT[n];
  return wort ? `${wort}mal` : `${n}-mal`;
}

/** „drei Werktagen" / „einem Werktag" — Dativ, wie er im Satz steht. */
export function werktageDativ(n: number): string {
  if (n === 1) return "einem Werktag";
  const wort = ZAHLWORT[n];
  return `${wort ?? n} Werktagen`;
}

/** „fünf Tagen" — Dativ für den Terminhinweis. */
function tageDativ(n: number): string {
  if (n === 1) return "einem Tag";
  const wort = ZAHLWORT[n];
  return `${wort ?? n} Tagen`;
}

/** Erster Buchstabe groß — die Bausteine tragen Kleinschreibung, der Satzanfang entscheidet. */
export function grossAmAnfang(text: string): string {
  return text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1);
}

/**
 * „8.400 €" bzw. „8.412,50 €". Ohne Nachkommastellen, wenn der Betrag glatt ist — so steht es in
 * Antoines Entwurf. Der Betrag kommt aus `offer_options.amount_total_cents`, nie aus dem Modell.
 *
 * Bewusst OHNE `style: "currency"`: dessen deutsche Ausgabe setzt zwischen Zahl und Zeichen ein
 * schmales geschütztes Leerzeichen (U+202F). Das ist im Quelltext unsichtbar, sieht in jedem
 * Vergleich wie ein normales Leerzeichen aus und hat beim ersten Testlauf genau das gemacht:
 * zwei identisch aussehende Zeichenketten, die nicht gleich sind. Der Satz wird gelesen und
 * gesucht — er bekommt ein gewöhnliches Leerzeichen.
 */
export function betragText(cents: number): string {
  const glatt = cents % 100 === 0;
  const zahl = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: glatt ? 0 : 2,
    maximumFractionDigits: glatt ? 0 : 2,
  }).format(cents / 100);
  return `${zahl} €`;
}

/* ────────────────────────────── Satzbau ───────────────────────────────────────────────────── */

/** Wie ein Baustein an den vorigen anschließt. */
export type Anschluss = "satz" | "komma" | "strich";

interface GesetzterBaustein extends StandBaustein {
  anschluss: Anschluss;
}

/**
 * Sammelt die Teilsätze und setzt daraus den Satz zusammen. Bewusst ein kleiner Bau und keine
 * Vorlage mit Platzhaltern: ein Platzhalter, der leer bleibt, erzeugt genau die halben Sätze,
 * die P0.3 verbietet. Hier entsteht ein Teilsatz nur, wenn seine Tatsache da ist.
 */
export class SatzBau {
  private readonly teile: GesetzterBaustein[] = [];

  /**
   * Nimmt den Teilsatz auf. `null` oder leer heißt: die Tatsache fehlt, also fehlt der Teilsatz —
   * die Aufrufer geben genau dann `null` zurück. Dass der genannte Beleg wirklich trägt, prüft
   * die Ratsche in `apps/api/test/anfrage-stand.test.ts`; hier wird nichts stillschweigend
   * weggelassen, was ein Aufrufer für gesetzt hielt.
   */
  add(text: string | null, belege: readonly string[], anschluss: Anschluss = "satz"): this {
    if (text === null || text.trim() === "") return this;
    this.teile.push({ text, belege, anschluss });
    return this;
  }

  /** Nimmt einen Teilsatz auf, der ausdrücklich das FEHLEN der genannten Felder aussagt. */
  addFehlt(text: string, belege: readonly string[], anschluss: Anschluss = "satz"): this {
    this.teile.push({ text, belege, anschluss, belegtAbwesenheit: true });
    return this;
  }

  /**
   * Fügt die Teile zusammen; Satzzeichen und Großschreibung entstehen hier, nicht in den
   * Bausteinen. Der Baustein trägt danach GENAU den Wortlaut, der auch im Satz steht — sonst
   * könnte die Ratsche nicht prüfen, dass die Bausteine wirklich der Satz sind.
   */
  private bauen(): { satz: string; kopf: string; rest: string; teile: StandBaustein[] } {
    let satz = "";
    /**
     * Der Kopf steht fest, sobald der ERSTE Satz abgeschlossen ist — also genau in dem Moment, in
     * dem ein Baustein mit Anschluss „satz" den nächsten beginnt. Danach wächst nur noch der Rest.
     */
    let kopf: string | null = null;
    const teile: StandBaustein[] = [];
    for (const teil of this.teile) {
      let wortlaut: string;
      if (satz === "") {
        wortlaut = grossAmAnfang(teil.text);
        satz = wortlaut;
      } else if (teil.anschluss === "komma") {
        wortlaut = teil.text;
        satz = `${satz}, ${wortlaut}`;
      } else if (teil.anschluss === "strich") {
        wortlaut = teil.text;
        satz = `${satz} — ${wortlaut}`;
      } else {
        wortlaut = grossAmAnfang(teil.text);
        const abgeschlossen = punktAmEnde(satz);
        if (kopf === null) kopf = abgeschlossen;
        satz = `${abgeschlossen} ${wortlaut}`;
      }
      teile.push(
        teil.belegtAbwesenheit
          ? { text: wortlaut, belege: teil.belege, belegtAbwesenheit: true }
          : { text: wortlaut, belege: teil.belege },
      );
    }
    const fertig = satz === "" ? "" : punktAmEnde(satz);
    // Nur ein Satz ⇒ er IST der Kopf, und der Rest ist leer. `satz` bleibt in jedem Fall
    // „Kopf plus Rest" — kein Aufrufer muss etwas anders zusammensetzen als bisher.
    const kopfFertig = kopf ?? fertig;
    const rest = kopfFertig === fertig ? "" : fertig.slice(kopfFertig.length).trimStart();
    return { satz: fertig, kopf: kopfFertig, rest, teile };
  }

  get bausteine(): StandBaustein[] {
    return this.bauen().teile;
  }

  get satz(): string {
    return this.bauen().satz;
  }

  get kopf(): string {
    return this.bauen().kopf;
  }

  get rest(): string {
    return this.bauen().rest;
  }
}

/**
 * Setzt den Schlusspunkt — außer der Satz endet schon auf einem. Das ist keine Kosmetik: ein
 * deutsches Kurzdatum endet selbst auf einem Punkt („zuletzt am 27.07."), und ein zweiter
 * daneben sähe nach einem Fehler aus.
 */
function punktAmEnde(satz: string): string {
  return /[.!?]$/.test(satz) ? satz : `${satz}.`;
}

/* ────────────────────────────── Wiederkehrende Teilsätze ───────────────────────────────────── */

interface Umfeld {
  jetzt: Date;
  zeitzone: string;
  bundesland: string;
}

/**
 * „Angebot am 24.07. verschickt" — ohne Punkt, damit ein Komma anschließen kann.
 *
 * `mitSubjekt: false` liefert „am 24.07. verschickt". Gebraucht wird das, seit der Kopf den Fall
 * benennt (§ 12.3): steht „Angebot nicht zugestellt." vorn, wäre „Angebot am 24.07. verschickt"
 * dahinter das zweite Subjekt in zwei Sätzen — dieselbe Tatsache, nur doppelt angesagt.
 */
function versendetTeil(f: AnfrageFakten, u: Umfeld, mitSubjekt = true): string | null {
  if (!istBelegt(f.angebot.versendetAm)) return null;
  const datum = datumKurz(f.angebot.versendetAm as Date, u.jetzt, u.zeitzone);
  if (!datum) return null;
  return mitSubjekt ? `Angebot am ${datum} verschickt` : `am ${datum} verschickt`;
}

/** „Einmal nachgefasst." — die Zahl kommt aus `events.followup_stage`. */
function nachgefasstTeil(f: AnfrageFakten): string | null {
  const n = f.angebot.nachfassungen;
  if (!istBelegt(n)) return null;
  return `${grossAmAnfang(malWort(n as number))} nachgefasst`;
}

/**
 * „Keine Antwort seit drei Werktagen." — nur ab einem vollen Werktag. `seit` ist der Versand des
 * Angebots (Zeilen 7/9) oder der Nachfrage (Zeilen 17/18).
 */
function ohneAntwortTeil(seit: Date | null, u: Umfeld): string | null {
  if (!istBelegt(seit)) return null;
  const werktage = werktageSeit(seit as Date, u.jetzt, u.bundesland, u.zeitzone);
  return werktage >= 1 ? `Keine Antwort seit ${werktageDativ(werktage)}` : null;
}

/** „Der Termin ist in fünf Tagen." / „Der Wunschtermin ist vorbei." */
function terminTeil(f: AnfrageFakten, u: Umfeld): string | null {
  if (!istBelegt(f.anfrage.terminAm)) return null;
  const tage = kalendertageBis(f.anfrage.terminAm as Date, u.jetzt, u.zeitzone);
  if (tage === null) return null;
  if (tage < 0) return "Der Wunschtermin ist vorbei";
  if (tage === 0) return "Der Termin ist heute";
  if (tage === 1) return "Der Termin ist morgen";
  if (tage <= TERMIN_HINWEIS_TAGE) return `Der Termin ist in ${tageDativ(tage)}`;
  return null;
}

/** „Angenommen am 26.07. — ‚Fingerfood Deluxe', 8.400 €" — jeder Bestandteil einzeln belegt. */
function annahmeTeile(bau: SatzBau, f: AnfrageFakten, u: Umfeld): void {
  const datum = istBelegt(f.angebot.angenommenAm)
    ? datumKurz(f.angebot.angenommenAm as Date, u.jetzt, u.zeitzone)
    : null;
  bau.add(datum ? `Angenommen am ${datum}` : null, ["angebot.angenommenAm"]);

  let ersteFortsetzung = datum !== null;
  const fortsetzung = (): Anschluss => {
    if (ersteFortsetzung) {
      ersteFortsetzung = false;
      return "strich";
    }
    return "komma";
  };

  if (istBelegt(f.angebot.gewaehlteOption)) {
    bau.add(`‚${(f.angebot.gewaehlteOption as string).trim()}'`, ["angebot.gewaehlteOption"], fortsetzung());
  }
  if (istBelegt(f.angebot.betragCents)) {
    bau.add(betragText(f.angebot.betragCents as number), ["angebot.betragCents"], fortsetzung());
  }
}

/* ────────────────────────────── Aktionen ───────────────────────────────────────────────────── */

/**
 * Die Aktionen, die in den bestehenden Angebots-Builder führen (`/veranstaltungen/<id>/angebot`).
 * Beide betreffen dasselbe Dokument: die eine füllt es, die andere schickt es weg.
 *
 * ABSICHTLICH weiterhin `/veranstaltungen/...` (nicht `/vorgaenge/...`): dieses Paket ist Backend-
 * Code (`packages/anfrage-stand`, mit `apps/api/test/anfrage-stand.test.ts` als Vertrag), F0
 * (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md Teil B2) ist ausdrücklich NUR das Frontend-Paket
 * ohne Backend-Änderungen. `apps/web/src/pages/inquiries.tsx#fuehreAktionAus` uebersetzt dieses
 * Alt-Ziel beim Navigieren auf die neue Adresse (`lib/pfade.ts#uebersetzeAltesZiel`); die App.tsx-
 * Splat-Umleitung faengt ausserdem jeden direkt aufgerufenen Alt-Link ab. Der Feld-Wortlaut hier
 * aendert sich erst mit dem Backend-Arbeitspaket dieses Plans.
 */
const ZIEL_ANGEBOTS_BUILDER: ReadonlySet<AktionSchluessel> = new Set<AktionSchluessel>([
  "angebot_bearbeiten",
  "angebot_senden",
]);

/** Ziele: die Zeile navigiert zu dem, was es schon gibt (§ 7). */
const ZIEL_UEBER_EVENT: ReadonlySet<AktionSchluessel> = new Set<AktionSchluessel>([
  "angebot_bearbeiten",
  "angebot_senden",
  "adresse_korrigieren",
  "nachfassen",
  "verloren_schliessen",
  "anzahlung_anfordern",
  "veranstaltung_oeffnen",
]);

/**
 * Baut die eine Aktion. Fehlt die Veranstaltung (kann passieren, solange noch kein Angebot
 * existiert), führt die Aktion auf die Anfragen-Detailseite statt ins Leere — ein toter Link
 * wäre schlimmer als ein Umweg.
 */
function aktionBauen(schluessel: AktionSchluessel, f: AnfrageFakten): StandAktion {
  const eventId = f.angebot.eventId;
  const ziel =
    ZIEL_UEBER_EVENT.has(schluessel) && istBelegt(eventId)
      ? ZIEL_ANGEBOTS_BUILDER.has(schluessel)
        ? `/veranstaltungen/${eventId}/angebot`
        : `/veranstaltungen/${eventId}`
      : `/anfragen/${f.anfrage.id}`;
  return { schluessel, text: AKTION_TEXT[schluessel], ziel };
}

/* ────────────────────────────── Heiße Vorgänge (Buchungsstrecke D2) ────────────────────────── */

/** Was die Regel „heiß“ braucht — bewusst klein, damit der Nachfass-Sweep sie ohne Board nutzt. */
export interface HeissTatsachen {
  /** `events.status = 'offer_sent'`. */
  angebotOffen: boolean;
  /** `events.offer_view_count`; `null` = nicht erhoben (dann nie heiß). */
  aufrufe: number | null | undefined;
  /**
   * Einblicke (Migration 730): Aufrufe seit dem letzten Versand. Ist es gesetzt (auch `null`),
   * entscheidet es statt `aufrufe` — ein neu verschicktes Angebot beginnt bei null.
   */
  aufrufeSeitVersand?: number | null | undefined;
  /** Eine Variante ist gewählt (`offer_options.is_chosen`). */
  gewaehlt: boolean;
  /** Testanfrage (`inquiries.is_test`) bzw. Probelauf. */
  istTest: boolean;
  /** Abgelehnt, abgesagt, „keine Antwort“, durchgeführt oder verbindlich gebucht. */
  beendet: boolean;
}

/**
 * Buchungsstrecke D2 — die EINE Regel für „heiß“: Angebot liegt offen beim Gast, mindestens
 * `HEISS_AUFRUFE` Aufrufe, keine gewählte Variante, keine Testanfrage, nichts entschieden.
 *
 * Rein: keine Datenbank, keine Uhr. Board (`leiteStandAb`, Zeile 19) und Nachfass-Sweep
 * (`apps/api/src/lib/followup-sweep.ts`) rufen dieselbe Funktion — ein heißer Vorgang steht oben
 * mit „Jetzt anrufen“ UND bekommt keinen weiteren automatischen Nachfass-Vorschlag.
 */
export function istHeisserVorgang(t: HeissTatsachen): boolean {
  if (!t.angebotOffen || t.gewaehlt || t.istTest || t.beendet) return false;
  const n = t.aufrufeSeitVersand !== undefined ? t.aufrufeSeitVersand : t.aufrufe;
  return typeof n === "number" && Number.isFinite(n) && n >= HEISS_AUFRUFE;
}

/**
 * `tel:`-Ziel aus einer frei eingegebenen Nummer: nur Ziffern und ein führendes `+` bleiben
 * („0171 / 123 45-6“ → `tel:0171123456`). Weniger als drei Ziffern sind keine Nummer — dann
 * `null`, und die Zeile sagt „Keine Nummer hinterlegt“, statt einen toten Anruf anzubieten.
 */
export function telefonZiel(telefon: string | null | undefined): string | null {
  if (typeof telefon !== "string") return null;
  const roh = telefon.trim();
  const ziffern = roh.replace(/\D/g, "");
  if (ziffern.length < 3) return null;
  return `tel:${roh.startsWith("+") ? "+" : ""}${ziffern}`;
}

/* ────────────────────────────── Die zeitliche Lage: Chip und Sortierung ────────────────────── */

/**
 * Welche zeitliche Tatsache eine Zeile bestimmt. Bewusst **eine** Lage je Zeile: aus ihr folgen
 * Chip UND Sortierschlüssel, damit beide nie verschiedene Auskünfte geben. Träfen mehrere
 * Tatsachen zu, entschiede sonst der Chip anders als die Reihenfolge, in der die Zeile steht.
 */
type ZeitLage = "termin_nah" | "termin_vorbei" | "nachfassen_faellig" | "keine";

interface ZeitlicheLage {
  art: ZeitLage;
  /** Kalendertage bis zum Wunschtermin (negativ = vorbei); `null` ohne belegten Termin. */
  tageBisTermin: number | null;
  /** Volle Werktage seit dem Versand ohne Antwort. 0, wo gar nicht auf Antwort gewartet wird. */
  werktageOhneAntwort: number;
}

/**
 * Bestimmt die Lage aus denselben Tatsachen und Fristen, aus denen auch der Satz entsteht —
 * `TERMIN_HINWEIS_TAGE` und `NACHFASS_WERKTAGE`, keine zweite Grenze.
 *
 * Zwei Festlegungen stecken darin:
 *
 *  1. **Ein abgeschlossener Vorgang trägt keine Frist mehr.** Bei einer Absage ist der vergangene
 *     Wunschtermin wahr, aber keine Auskunft: nachzufassen ist nichts, zu terminieren auch nicht.
 *  2. **Der Termin schlägt die Nachfassfrist.** Beides kann zugleich gelten; genannt wird der
 *     Termin, weil er nicht verschiebbar ist — und weil „Nachfassen" in derselben Zeile ohnehin
 *     schon als Aktion steht (§ 12.4). Der Chip würde sie sonst bloß wiederholen.
 */
function zeitlicheLage(f: AnfrageFakten, u: Umfeld, regel: StandRegel): ZeitlicheLage {
  const abgeschlossen = istBelegt(f.angebot.abgelehntAm) || istBelegt(f.angebot.abgesagtAm);

  const tageBisTermin =
    !abgeschlossen && istBelegt(f.anfrage.terminAm)
      ? kalendertageBis(f.anfrage.terminAm as Date, u.jetzt, u.zeitzone)
      : null;

  /**
   * Gewartet wird nur, wo etwas rausgegangen, angekommen und noch nicht entschieden ist. Bei
   * einer fehlgeschlagenen Zustellung ist nicht das Nachfassen dran, sondern die Adresse.
   */
  const wartetAufAntwort =
    !abgeschlossen &&
    istBelegt(f.angebot.versendetAm) &&
    !istBelegt(f.angebot.zustellungFehlgeschlagen) &&
    !istBelegt(f.angebot.angenommenAm);
  /**
   * Zeilen 17/18: auch eine Nachfrage ohne Angebot wartet auf Antwort — dieselbe Frist, derselbe
   * Chip. Nur wenn genau diese Zeilen gegriffen haben: sonst trüge ein erledigter Vorgang mit
   * einer alten Nachfrage eine Nachfassfrist.
   */
  const wartetAufNachfrage =
    !wartetAufAntwort && (regel === "nachfrage_wartet" || regel === "nachfrage_nachfassen");
  const werktageOhneAntwort = wartetAufAntwort
    ? werktageSeit(f.angebot.versendetAm as Date, u.jetzt, u.bundesland, u.zeitzone)
    : wartetAufNachfrage
      ? werktageSeit(f.angebot.nachfrageAm as Date, u.jetzt, u.bundesland, u.zeitzone)
      : 0;

  const art: ZeitLage =
    tageBisTermin !== null && tageBisTermin < 0
      ? "termin_vorbei"
      : tageBisTermin !== null && tageBisTermin <= TERMIN_HINWEIS_TAGE
        ? "termin_nah"
        : werktageOhneAntwort >= NACHFASS_WERKTAGE
          ? "nachfassen_faellig"
          : "keine";

  return { art, tageBisTermin, werktageOhneAntwort };
}

/**
 * Der Wortlaut des Chips. **Ziffern statt Zahlwort** — anders als im Satz: der Chip ist ein
 * Etikett und kein Satz, und Antoines Entwurf schreibt ihn wörtlich als `Termin in 4 Tagen`.
 * „heute" und „morgen" folgen derselben Unterscheidung, die `terminTeil` im Satz schon trifft.
 */
function chipText(lage: ZeitlicheLage): string | null {
  switch (lage.art) {
    case "termin_vorbei":
      return "Wunschtermin vorbei";
    case "termin_nah": {
      const tage = lage.tageBisTermin ?? 0;
      if (tage === 0) return "Termin heute";
      if (tage === 1) return "Termin morgen";
      return `Termin in ${tage} Tagen`;
    }
    case "nachfassen_faellig":
      return "Nachfassen fällig";
    case "keine":
      return null;
  }
}

/** Echte Fehler zuerst (§ 6, Schlusssatz) — normales Warten ist keine Dringlichkeit. */
const DRINGLICHKEIT_RANG: Record<Dringlichkeit, number> = { fehler: 0, wichtig: 1, normal: 2 };

/**
 * Ein naher Termin ist die dringlichste zeitliche Lage, eine gerissene Nachfassfrist die
 * nächste. Ein **vergangener** Termin steht dahinter: er ist die wichtigere Auskunft, aber
 * keine Dringlichkeit mehr — da ist nichts mehr rechtzeitig zu schaffen.
 */
const LAGE_RANG: Record<ZeitLage, number> = {
  termin_nah: 0,
  nachfassen_faellig: 1,
  termin_vorbei: 2,
  keine: 3,
};

/**
 * „Dringendes zuerst" als eine Zahl, von grob nach fein: Dringlichkeit, dann die zeitliche Lage,
 * dann innerhalb der Lage die Nähe bzw. die Wartezeit. Aufsteigend sortiert — kleiner steht oben.
 * Die drei Stellen sind bewusst so weit auseinander, dass die feine Ordnung die grobe nie kippt.
 */
function sortierschluessel(dringlichkeit: Dringlichkeit, lage: ZeitlicheLage): number {
  const fein = (() => {
    switch (lage.art) {
      // Näher am Termin ⇒ weiter oben.
      case "termin_nah":
        return Math.min(9_999, Math.max(0, lage.tageBisTermin ?? 0));
      // Länger ohne Antwort ⇒ weiter oben.
      case "nachfassen_faellig":
        return Math.max(0, 9_999 - lage.werktageOhneAntwort);
      // Gerade erst vorbei ⇒ weiter oben als lange vorbei.
      case "termin_vorbei":
        return Math.min(9_999, Math.max(0, -(lage.tageBisTermin ?? 0)));
      case "keine":
        return 0;
    }
  })();
  return DRINGLICHKEIT_RANG[dringlichkeit] * 100_000 + LAGE_RANG[lage.art] * 10_000 + fein;
}

/* ────────────────────────────── Die Regel ──────────────────────────────────────────────────── */

/**
 * Leitet aus den Tatsachen einer Anfrage ab, wer dran ist, was der Stand ist und welche EINE
 * Handlung folgt — und hängt die zeitliche Lage daran (Chip und Sortierschlüssel, § 12.4).
 *
 * Der Satzteil steckt in `standAusRegel`; hier kommt nur das dazu, was für JEDE der dreizehn
 * Zeilen gleich gebildet wird. So steht die Ableitung an einer Stelle statt dreizehnmal.
 */
export function leiteStandAb(fakten: AnfrageFakten): AnfrageStand {
  const u: Umfeld = {
    jetzt: fakten.jetzt,
    zeitzone: fakten.zeitzone ?? ZEITZONE_STANDARD,
    bundesland: fakten.bundesland,
  };
  const stand = standAusRegel(fakten);
  const lage = zeitlicheLage(fakten, u, stand.regel);
  const angebotVersendetAm = istBelegt(fakten.angebot.versendetAm)
    ? datumKurz(fakten.angebot.versendetAm as Date, u.jetzt, u.zeitzone)
    : null;
  // D2: ein heißer Vorgang steht OBEN in „Du bist dran“ — vor jedem anderen Schlüssel (die sind
  // alle ≥ 0), untereinander mehr Aufrufe zuerst. Den Chip „Nachfassen fällig“ trägt er nicht:
  // statt einer weiteren Mail steht der Anruf an.
  const heiss = stand.regel === "heiss_anrufen";
  return {
    ...stand,
    chip: heiss && lage.art === "nachfassen_faellig" ? null : chipText(lage),
    angebotVersendetAm,
    sortierung: heiss
      ? -1 - Math.min(9_998, Math.max(0, fakten.angebot.aufrufeSeitVersand ?? fakten.angebot.aufrufe ?? 0))
      : sortierschluessel(stand.dringlichkeit, lage),
  };
}

/**
 * Die dreizehn Zeilen aus § 6, in dieser Reihenfolge geprüft:
 *
 *  1. **Erledigt zuerst.** Abgelehnt/abgesagt und angenommen sind Endzustände; alles davor
 *     (unversendet, wartend) ist dann Geschichte und darf die Zeile nicht mehr beschreiben.
 *  2. **Der Automat sitzt gerade dran (Zeile 14).** Ein aktiver Erzeugungs-Anspruch
 *     (`inquiries.status = 'generating'`) übersteuert JEDE der folgenden Zeilen — egal ob noch
 *     gar kein Angebot existiert oder gerade eine neue Variante zu einem bereits versendeten
 *     entsteht (K5.2/Gast-Antwort v2). Ohne diesen Vorrang stünde eine veraltete Aussage da,
 *     während der Automat längst etwas anderes tut.
 *  3. **Fehler vor Warten.** Eine fehlgeschlagene Zustellung schlägt jede Wartelogik — ein
 *     Angebot, das nie ankam, wartet nicht, es liegt beim Betreiber.
 *  4. **Leer vor fertig.** Eine Angebotsvariante ohne eine einzige Position (Zeile 13) wird VOR
 *     Zeile 4 geprüft. Andernfalls sagte die Zeile weiter „Angebot fertig … nur noch senden" und
 *     schickte den Betreiber auf einen Knopf, hinter dem ein leeres Dokument liegt.
 *  5. **Kein Angebot** — die drei Vollständigkeitsfälle in der Reihenfolge der Tabelle (AP6: die
 *     frühere Zeile 6 „kein Stand/Import" entfiel mit Migration 412 — seither hat jede Anfrage
 *     sofort einen, ggf. leeren, Stamm-Vorgang; ein Vorgang ohne hinterlegten Stand ist seither
 *     unerreichbar, nicht mehr nur ein Randfall).
 *  6. **Versendet** — verloren, nachfassen, warten.
 *
 * Zeile 8 („nie geöffnet") setzt eine gezählte Null voraus (`aufrufe === 0`). Ist das
 * Aufruf-Tracking für diese Zeile gar nicht erhoben (`aufrufe === null`), gilt sie NICHT als „nie
 * geöffnet" — eine fehlende Messung ist keine Beobachtung.
 */
function standAusRegel(
  fakten: AnfrageFakten,
): Omit<AnfrageStand, "chip" | "sortierung" | "angebotVersendetAm"> {
  const u: Umfeld = {
    jetzt: fakten.jetzt,
    zeitzone: fakten.zeitzone ?? ZEITZONE_STANDARD,
    bundesland: fakten.bundesland,
  };
  const { anfrage, angebot, anzahlung } = fakten;

  /* ── Zeile 16: keine Antwort ───────────────────────────────────────────────────────────── */
  /*
   * VOR Zeile 12, und das ist Absicht: „keine Antwort" ist ein eigener Ausgang, keine Unterart
   * der Absage. Stuende sie danach, traege der Vorgang bei gleichzeitig vorhandener Absage-Spur
   * das falsche Wort — und genau die Unterscheidung („verloren" vs. „nie beantwortet") ist der
   * Grund, warum der Betrieb diesen Zustand ueberhaupt setzt.
   *
   * Gruppe `abgelehnt` (= Anzeige „Entschieden"), damit die Zeile aus „Du bist dran"
   * VERSCHWINDET. Das war der ganze Befund: der Klick des Betreibers hatte sichtbar keine
   * Wirkung, die Karte blieb stehen, als waere nichts entschieden.
   *
   * KEINE offene Handlung (`aktion: null`): anders als bei Zeile 12 wird hier kein Grund
   * nachgefragt. Der Grund IST der Zustand — niemand hat geantwortet.
   */
  if (istBelegt(angebot.ohneAntwortSeit)) {
    const bau = new SatzBau();
    const datum = datumKurz(angebot.ohneAntwortSeit as Date, u.jetzt, u.zeitzone);
    bau.add(datum ? `Keine Antwort — seit ${datum}` : "Keine Antwort", ["angebot.ohneAntwortSeit"]);
    bau.add(versendetTeil(fakten, u, false), ["angebot.versendetAm"]);
    return {
      regel: "ohne_antwort",
      gruppe: "abgelehnt",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      aktion: null,
      dringlichkeit: "normal",
      bausteine: bau.bausteine,
    };
  }

  /* ── Zeile 12: abgelehnt oder abgesagt ─────────────────────────────────────────────────── */
  if (istBelegt(angebot.abgelehntAm) || istBelegt(angebot.abgesagtAm)) {
    const bau = new SatzBau();
    // § 12.3: der Fall ist die Absage, nicht der Versand — sie trägt also den Kopf. Der notierte
    // Grund gehört mit dazu („abgelehnt, weil …"), der Versand rutscht in den Rest.
    if (istBelegt(angebot.abgelehntAm)) {
      const datum = datumKurz(angebot.abgelehntAm as Date, u.jetzt, u.zeitzone);
      bau.add(datum ? `Abgelehnt am ${datum}` : null, ["angebot.abgelehntAm"]);
    } else {
      const datum = datumKurz(angebot.abgesagtAm as Date, u.jetzt, u.zeitzone);
      bau.add(datum ? `Abgesagt am ${datum}` : null, ["angebot.abgesagtAm"]);
    }
    // Solange kein Grund notiert ist, endet der KOPF nach dem Datum — es wird nichts vermutet und
    // nichts angehängt (P0.3, wörtlich). Erst der notierte Grund hängt sich an.
    if (istBelegt(angebot.ablehnungsgrund)) {
      bau.add((angebot.ablehnungsgrund as string).trim(), ["angebot.ablehnungsgrund"], "strich");
    }
    bau.add(versendetTeil(fakten, u, false), ["angebot.versendetAm"]);
    return {
      regel: "abgelehnt",
      gruppe: "abgelehnt",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      // Ohne notierten Grund bleibt genau eine Handlung offen; mit Grund ist nichts mehr zu tun.
      aktion: istBelegt(angebot.ablehnungsgrund) ? null : aktionBauen("grund_notieren", fakten),
      dringlichkeit: "normal",
      bausteine: bau.bausteine,
    };
  }

  /* ── Zeilen 10/11: angenommen ──────────────────────────────────────────────────────────── */
  if (istBelegt(angebot.angenommenAm)) {
    // § 12.3: Bei Zeile 10 ist der Fall die offene Anzahlung — sie steht vorn, die Annahme wird
    // zum Beleg dahinter. Bei Zeile 11 ist der Fall die Buchung selbst; dort bleibt die Annahme
    // der Kopf und „Anzahlung angefordert" der Beleg.
    if (istBelegt(anzahlung.ueberfaelligSeit)) {
      // Rot, weil es ein echter Fehler ist und kein Warten (§ 6, Schlusssatz).
      const bau = new SatzBau();
      const datum = datumKurz(anzahlung.ueberfaelligSeit as Date, u.jetzt, u.zeitzone);
      bau.add(datum ? `Anzahlung seit dem ${datum} überfällig` : null, ["anzahlung.ueberfaelligSeit"]);
      annahmeTeile(bau, fakten, u);
      return {
        regel: "gebucht_anzahlung",
        gruppe: "gebucht",
        satz: bau.satz,
        kopf: bau.kopf,
        rest: bau.rest,
        aktion: aktionBauen("anzahlung_anfordern", fakten),
        dringlichkeit: "fehler",
        bausteine: bau.bausteine,
      };
    }

    if (!istBelegt(anzahlung.angefordert)) {
      const bau = new SatzBau();
      bau.addFehlt("Anzahlung noch nicht angefordert", ["anzahlung.angefordert"]);
      annahmeTeile(bau, fakten, u);
      return {
        regel: "gebucht_anzahlung",
        gruppe: "gebucht",
        satz: bau.satz,
        kopf: bau.kopf,
        rest: bau.rest,
        aktion: aktionBauen("anzahlung_anfordern", fakten),
        dringlichkeit: "wichtig",
        bausteine: bau.bausteine,
      };
    }

    const bau = new SatzBau();
    annahmeTeile(bau, fakten, u);
    bau.add("Anzahlung angefordert", ["anzahlung.angefordert"]);
    return {
      regel: "gebucht_erledigt",
      gruppe: "gebucht",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      aktion: aktionBauen("veranstaltung_oeffnen", fakten),
      dringlichkeit: "normal",
      bausteine: bau.bausteine,
    };
  }

  /**
   * ── Zeile 15: entschieden, ohne dass eine Zusage am Angebot hängt ─────────────────────────
   *
   * Antoine, 20.09.2026, mit Bildschirmfoto: „Veranstaltungen, die als ‚Durchgeführt‘ gelten,
   * dürfen nicht in der Spalte ‚Du bist dran‘ auftauchen."
   *
   * Der gemeldete Vorgang war durchgeführt und vollständig bezahlt — und stand trotzdem unter
   * „Du bist dran" mit „Gästezahl fehlt · Angebot erstellen". Grund: die Zeilen 10/11 hängen
   * ausschließlich an `offer_options.chosen_at`. Wer telefonisch oder von Hand bucht, hat nie ein
   * Angebot; die Zusage steht dann nur am Vorgangsstatus. Ohne eine Zeile dafür fiel der Vorgang
   * durch bis zu „kein Angebot, Gästezahl fehlt" — und die Liste forderte zu einer Handlung auf,
   * die vier Wochen nach der Veranstaltung niemanden mehr interessiert.
   *
   * Gemessen (20.09.2026, rein lesend gegen Produktion): ein durchgeführter Vorgang und zehn
   * „zugesagte" ohne Angebot. Elf Zeilen, die dauerhaft falsch in der Arbeitsspalte standen.
   *
   * **Diese Zeile steht bewusst NACH den Zeilen 10/11 und nicht davor.** Trägt eine Variante die
   * Zusage, ist deren Satz der genauere („Angenommen am …, Anzahlung angefordert"); erst wenn es
   * ihn nicht gibt, ist der Status die beste verfügbare Auskunft. Und sie steht VOR Zeile 14: ein
   * stehengebliebenes `generating`-Flag darf einen abgeschlossenen Vorgang nicht zurück in die
   * Arbeitsspalte holen — dieselbe Überlegung wie bei „Erledigt zuerst".
   */
  if (angebot.durchgefuehrt || angebot.verbindlichGebucht) {
    const bau = new SatzBau();
    // Zwei Tatsachen, zwei Sätze: „durchgeführt" sagt mehr als „gebucht" und wird deshalb nicht
    // unter ihm eingeebnet. Vermutet wird nichts — beides steht so am Vorgang.
    if (angebot.durchgefuehrt) {
      bau.add("Durchgeführt", ["angebot.durchgefuehrt"]);
    } else {
      bau.add("Verbindlich gebucht", ["angebot.verbindlichGebucht"]);
    }
    bau.add(terminTeil(fakten, u), ["anfrage.terminAm"]);
    return {
      regel: "verbindlich_ohne_zusage",
      gruppe: "gebucht",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      // Dieselbe Aktion wie Zeile 11: es ist nichts mehr zu veranlassen, man kann nur hineinsehen.
      aktion: aktionBauen("veranstaltung_oeffnen", fakten),
      dringlichkeit: "normal",
      bausteine: bau.bausteine,
    };
  }

  /**
   * ── Zeile 14: der Automat sitzt gerade dran ───────────────────────────────────────────────
   *
   * Bewusst HIER geprüft — NACH „erledigt zuerst" (Zeile 12, Zeilen 10/11), aber VOR jeder
   * anderen Zeile: der CAS-Claim in `runInquiryAutoDraft` (`apps/api/src/lib/inquiry-auto-draft.ts`,
   * "CAS 'new'|'quoted' -> 'generating'") kann sowohl eine Anfrage OHNE Angebot treffen (der
   * naheliegende Fall: ein neuer Lead, für den Zeile 1-3/6 sonst gegriffen hätten) als auch eine
   * bereits BEANTRAGTE ('quoted', K5.2/Gast-Antwort v2 — der Kunde hat geantwortet, der Automat
   * baut eine neue Variante). Im zweiten Fall existiert `angebot.entwurfSeit` schon, und ohne
   * diese Zeile VOR Zeile 4/5/13 stünde weiterhin „Angebot fertig … nur noch senden" oder
   * „ohne Positionen" da, während in Wahrheit gerade eine neue Fassung entsteht — derselbe
   * Fehler wie der, den Zeile 13 (P5.1) für die leere Variante schon einmal behoben hat.
   *
   * „Erledigt zuerst" bleibt davor stehen: ein gebuchter oder abgelehnter Vorgang bleibt das,
   * auch wenn irgendwo ein stehen gebliebenes `generating`-Flag hinge (der Claim kommt laut CAS
   * nur aus 'new'/'quoted', beide Endzustände sollten es also praktisch nie erreichen — die
   * Reihenfolge ist trotzdem die sichere, nicht die bequeme).
   *
   * Kein `dringlichkeit: "wichtig"`: es ist gerade NICHT der Betreiber dran (§ 6, Schlusssatz —
   * Rot/Wichtig bleibt Fehlern und echtem Warten vorbehalten), sondern der Automat, und das soll
   * sich auch optisch nicht wie ein liegen gebliebener Vorgang lesen.
   */
  if (anfrage.generierungLaeuft) {
    const bau = new SatzBau();
    bau.add("Angebot wird gerade erstellt", ["anfrage.generierungLaeuft"]);
    bau.add(terminTeil(fakten, u), ["anfrage.terminAm"]);
    return {
      regel: "wird_generiert",
      gruppe: "du_bist_dran",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      // Keine Aktion: es ist buchstäblich nichts zu tun, außer kurz zu warten (§ 6, Zeile 14) —
      // anders als Zeile 9 ("wartet") trägt die Oberfläche das aber sichtbar als Spinner vor,
      // nicht als "Nichts zu tun" (unterschieden am REGEL-Schlüssel, nicht an `aktion === null`).
      aktion: null,
      dringlichkeit: "normal",
      bausteine: bau.bausteine,
    };
  }

  /* ── Zeile 5: versendet, aber nicht angekommen ─────────────────────────────────────────── */
  if (istBelegt(angebot.versendetAm) && istBelegt(angebot.zustellungFehlgeschlagen)) {
    const bau = new SatzBau();
    // § 12.3: der Fall ist die gescheiterte Zustellung, nicht der Versand.
    bau.add("Angebot nicht zugestellt", ["angebot.zustellungFehlgeschlagen"]);
    bau.add(versendetTeil(fakten, u, false), ["angebot.versendetAm"]);
    bau.add("Adresse funktioniert nicht", ["angebot.zustellungFehlgeschlagen"], "strich");
    return {
      regel: "zustellung_fehlgeschlagen",
      gruppe: "du_bist_dran",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      aktion: aktionBauen("adresse_korrigieren", fakten),
      dringlichkeit: "fehler",
      bausteine: bau.bausteine,
    };
  }

  /**
   * ── Zeilen 17/18: Nachricht an den Kunden gesendet, ohne Angebot, noch keine Antwort ──────
   *
   * Antoine, 27.09.2026: eine aus MAESTRO gesendete Nachfrage (Freitext ohne Angebot, Rückfrage,
   * Antwort aus dem Posteingang) setzt die Karte auf „Kunde ist dran", bis der Kunde antwortet.
   * Vorher stand sie weiter unter „Du bist dran" mit „noch kein Angebot" — obwohl der Betrieb
   * gerade auf den Kunden wartete.
   *
   * Reihenfolge: NACH „erledigt zuerst" und Zeile 14, und nur ohne versendetes Angebot — ein
   * gesendetes Angebot hat Vorrang (Zeilen 5, 7–9). VOR den Zeilen 13, 4 und 1–3: sonst verdeckte
   * „Angebot ohne Positionen" oder „noch kein Angebot" die offene Frage an den Kunden.
   * Antwortet der Kunde, ist `nachfrageAm` leer (Abfrage) und die Zeile greift nicht mehr.
   */
  if (istBelegt(angebot.nachfrageAm) && !istBelegt(angebot.versendetAm)) {
    const seit = angebot.nachfrageAm as Date;
    const werktage = werktageSeit(seit, u.jetzt, u.bundesland, u.zeitzone);
    const datum = datumKurz(seit, u.jetzt, u.zeitzone);
    const bau = new SatzBau();
    const ohneAntwort = ohneAntwortTeil(seit, u);
    bau.add(ohneAntwort, ["angebot.nachfrageAm"]);
    bau.add(
      datum ? `Nachricht an den Kunden am ${datum} gesendet` : "Nachricht an den Kunden gesendet",
      ["angebot.nachfrageAm"],
    );
    // Am Sendetag selbst gibt es „keine Antwort seit …" noch nicht — dann sagt der Satz, worauf
    // gewartet wird.
    if (ohneAntwort === null) bau.add("wartet auf Antwort", ["angebot.nachfrageAm"], "komma");
    bau.add(terminTeil(fakten, u), ["anfrage.terminAm"]);
    const nachfassen = werktage >= NACHFASS_WERKTAGE;
    return {
      regel: nachfassen ? "nachfrage_nachfassen" : "nachfrage_wartet",
      gruppe: "kunde_ist_dran",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      aktion: nachfassen ? aktionBauen("nachfassen", fakten) : null,
      dringlichkeit: "normal",
      bausteine: bau.bausteine,
    };
  }

  /* ── Zeile 13: Angebot angelegt, aber ohne eine einzige Position ───────────────────────── */
  // MUSS vor Zeile 4 stehen: sonst greift „Angebot fertig … nur noch senden" weiterhin zuerst,
  // und genau das war der Befund aus P5.1.
  if (
    istBelegt(angebot.entwurfSeit) &&
    !istBelegt(angebot.versendetAm) &&
    !istBelegt(angebot.hatPositionen)
  ) {
    const bau = new SatzBau();
    // § 12.3: der Fall ist die leere Variante, das Anlegedatum ist der Beleg dafür.
    bau.addFehlt("Angebot ohne Positionen", ["angebot.hatPositionen"]);
    const datum = datumKurz(angebot.entwurfSeit as Date, u.jetzt, u.zeitzone);
    bau.add(datum ? `am ${datum} angelegt` : "angelegt", ["angebot.entwurfSeit"]);
    bau.add(terminTeil(fakten, u), ["anfrage.terminAm"]);
    return {
      regel: "angebot_leer",
      gruppe: "du_bist_dran",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      aktion: aktionBauen("angebot_bearbeiten", fakten),
      dringlichkeit: "wichtig",
      bausteine: bau.bausteine,
    };
  }

  /* ── Zeile 4: Angebot erstellt, aber nicht versendet ───────────────────────────────────── */
  if (istBelegt(angebot.entwurfSeit) && !istBelegt(angebot.versendetAm)) {
    const bau = new SatzBau();
    // § 12.3: der Fall ist, dass nichts rausgegangen ist. Woran man es sieht, steht dahinter —
    // und zwar weiterhin ohne erfundenen „fertiggestellt"-Zeitstempel (§ 4): der belegbare Bezug
    // ist das Anlegen des Entwurfs, „fertig" ruht auf den vorhandenen Positionen.
    bau.addFehlt("Entwurf nicht versendet", ["angebot.versendetAm"]);
    const datum = datumKurz(angebot.entwurfSeit as Date, u.jetzt, u.zeitzone);
    bau.add(
      datum ? `Angebot fertig, angelegt am ${datum}` : "Angebot fertig",
      ["angebot.entwurfSeit", "angebot.hatPositionen"],
    );
    bau.add(terminTeil(fakten, u), ["anfrage.terminAm"]);
    return {
      regel: "angebot_unversendet",
      gruppe: "du_bist_dran",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      aktion: aktionBauen("angebot_senden", fakten),
      dringlichkeit: "wichtig",
      bausteine: bau.bausteine,
    };
  }

  /* ── Zeilen 1–3: es gibt gar kein Angebot ──────────────────────────────────────────────── */
  if (!istBelegt(angebot.entwurfSeit) && !istBelegt(angebot.versendetAm)) {
    /**
     * § 12.3: „Noch kein Angebot erstellt" ist die GEMEINSAMKEIT dieser drei Zeilen und deshalb
     * gerade nicht das, was den Fall ausmacht. Der Kopf nennt, was die Zeile von den anderen
     * unterscheidet — die fehlende Gästezahl, die fehlende Nachricht, oder eben die
     * Vollständigkeit. Nur bei Zeile 3 bleibt das fehlende Angebot selbst der Fall.
     */
    const ohneAngebot = (bau: SatzBau) => bau.addFehlt("noch kein Angebot erstellt", ["angebot.entwurfSeit"]);

    // Zeile 1: ohne Gästezahl lässt sich kein Angebot rechnen — auch nicht mit KI.
    if (!istBelegt(anfrage.gaeste)) {
      const bau = new SatzBau();
      bau.addFehlt("die Gästezahl fehlt", ["anfrage.gaeste"]);
      ohneAngebot(bau);
      bau.add(terminTeil(fakten, u), ["anfrage.terminAm"]);
      return {
        regel: "kein_angebot_gaeste_fehlen",
        gruppe: "du_bist_dran",
        satz: bau.satz,
        kopf: bau.kopf,
        rest: bau.rest,
        aktion: aktionBauen("angebot_erstellen", fakten),
        dringlichkeit: "wichtig",
        bausteine: bau.bausteine,
      };
    }

    // Zeile 2: keine Nachricht mitgeschickt — es fehlt Wissen, nicht Arbeit. Der Anlass fuer sich
    // allein fuehrt NICHT hierher: die KI-Angebotserstellung kommt ohne ihn aus (Zeile 3), er
    // bleibt dort nur ein Hinweis im Satz, ohne die Aktion zu aendern — dieselbe Art
    // Differenzierung wie bei `generierungLaeuft` oben (eine Tatsache fehlt, die Handlung bleibt
    // trotzdem dieselbe). Fehlt VOR Migration 254 gemeldeter Bug: „Anlass unbekannt" allein loeste
    // faelschlich „✨ Rueckfrage schreiben" statt „✨ Angebot erstellen" aus.
    if (!istBelegt(anfrage.nachricht)) {
      const bau = new SatzBau();
      bau.addFehlt("keine Nachricht mitgeschickt", ["anfrage.nachricht"]);
      if (!istBelegt(anfrage.anlass)) bau.addFehlt("Anlass unbekannt", ["anfrage.anlass"]);
      ohneAngebot(bau);
      bau.add(terminTeil(fakten, u), ["anfrage.terminAm"]);
      return {
        regel: "kein_angebot_rueckfrage",
        gruppe: "du_bist_dran",
        satz: bau.satz,
        kopf: bau.kopf,
        rest: bau.rest,
        aktion: aktionBauen("rueckfrage_schreiben", fakten),
        dringlichkeit: "wichtig",
        bausteine: bau.bausteine,
      };
    }

    // Zeile 3: Nachricht liegt vor — das reicht der KI-Angebotserstellung, auch ohne Anlass.
    // Fehlt der Anlass trotzdem, bleibt das ein Hinweis im Satz (P0.3: kein Satz ohne Beleg),
    // aendert aber nicht die Aktion.
    const bau = new SatzBau();
    ohneAngebot(bau);
    if (istBelegt(anfrage.anlass)) {
      bau.add("alle Angaben liegen vor", ["anfrage.gaeste", "anfrage.nachricht", "anfrage.anlass"]);
    } else {
      bau.add("Nachricht und Gästezahl liegen vor", ["anfrage.gaeste", "anfrage.nachricht"]);
      bau.addFehlt("Anlass unbekannt", ["anfrage.anlass"]);
    }
    bau.add(terminTeil(fakten, u), ["anfrage.terminAm"]);
    return {
      regel: "kein_angebot_vollstaendig",
      gruppe: "du_bist_dran",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      aktion: aktionBauen("angebot_erstellen_ki", fakten),
      dringlichkeit: "wichtig",
      bausteine: bau.bausteine,
    };
  }

  /* ── Zeilen 7–9: versendet, keine Antwort ──────────────────────────────────────────────── */
  const werktage = werktageSeit(angebot.versendetAm as Date, u.jetzt, u.bundesland, u.zeitzone);
  const nieGeoeffnet = angebot.aufrufe === 0 && !istBelegt(angebot.zuletztAngesehenAm);
  const terminVorbei = (() => {
    if (!istBelegt(anfrage.terminAm)) return false;
    const tage = kalendertageBis(anfrage.terminAm as Date, u.jetzt, u.zeitzone);
    return tage !== null && tage < 0;
  })();

  /**
   * ── Zeile 19 (Buchungsstrecke D2): heiß — mehrfach angesehen, keine Zusage ────────────────
   *
   * Wer das Angebot `HEISS_AUFRUFE`-mal öffnet und nicht zusagt, wägt ab. Dann ist der Betrieb
   * dran, nicht der Gast: die Zeile wandert nach „Du bist dran“, steht dort oben
   * (`sortierung` in `leiteStandAb`) und bietet „Jetzt anrufen“ statt „✨ Nachfassen“ an. Der
   * Nachfass-Sweep legt für denselben Vorgang keine weitere Mail mehr ab (dieselbe Regel).
   *
   * Nach Zeile 5 (Zustellung) und allen Endzuständen, VOR den Zeilen 7–9: Zeile 8 setzt „nie
   * geöffnet“ voraus und kann nicht zugleich gelten.
   */
  if (
    istHeisserVorgang({
      angebotOffen: angebot.angebotOffen === true,
      aufrufe: angebot.aufrufe,
      aufrufeSeitVersand: angebot.aufrufeSeitVersand,
      gewaehlt: istBelegt(angebot.angenommenAm),
      istTest: anfrage.istTest === true,
      beendet: false, // die Endzustände sind oben schon ausgestiegen
    })
  ) {
    const heiss = new SatzBau();
    // Einblicke (730): gemeint sind die Aufrufe des AKTUELLEN Angebots („seit dem Versand“).
    heiss.add(
      `${(angebot.aufrufeSeitVersand !== undefined ? angebot.aufrufeSeitVersand : angebot.aufrufe) as number}× angesehen`,
      [angebot.aufrufeSeitVersand !== undefined ? "angebot.aufrufeSeitVersand" : "angebot.aufrufe"],
    );
    heiss.addFehlt("noch keine Zusage", ["angebot.angenommenAm"], "komma");
    heiss.add(versendetTeil(fakten, u), ["angebot.versendetAm"]);
    if (istBelegt(angebot.zuletztAngesehenAm)) {
      const datum = datumKurz(angebot.zuletztAngesehenAm as Date, u.jetzt, u.zeitzone);
      heiss.add(datum ? `zuletzt am ${datum}` : null, ["angebot.zuletztAngesehenAm"], "strich");
    }
    heiss.add(nachgefasstTeil(fakten), ["angebot.nachfassungen"]);
    const tel = telefonZiel(anfrage.telefon);
    if (tel === null) heiss.addFehlt("keine Nummer hinterlegt", ["anfrage.telefon"]);
    return {
      regel: "heiss_anrufen",
      gruppe: "du_bist_dran",
      satz: heiss.satz,
      kopf: heiss.kopf,
      rest: heiss.rest,
      // Ohne Nummer kein toter `tel:`-Link — der Vorgang, wo die Kontaktdaten stehen.
      aktion:
        tel !== null
          ? { schluessel: "jetzt_anrufen", text: AKTION_TEXT.jetzt_anrufen, ziel: tel }
          : aktionBauen("veranstaltung_oeffnen", fakten),
      dringlichkeit: "wichtig",
      bausteine: heiss.bausteine,
    };
  }

  const bau = new SatzBau();

  // Zeile 8: nie geöffnet, zweimal nachgefasst, Termin vorbei — hier ist nichts mehr zu holen.
  // § 12.3: der Fall ist, dass das Angebot nie geöffnet wurde; Versand, Nachfassungen und der
  // vergangene Termin sind das, woran man es sieht.
  if (nieGeoeffnet && (angebot.nachfassungen ?? 0) >= VERLOREN_NACHFASSUNGEN && terminVorbei) {
    bau.addFehlt("nie geöffnet", ["angebot.zuletztAngesehenAm"]);
    bau.add(versendetTeil(fakten, u), ["angebot.versendetAm"]);
    bau.add(nachgefasstTeil(fakten), ["angebot.nachfassungen"]);
    bau.add("Der Wunschtermin ist vorbei", ["anfrage.terminAm"]);
    return {
      regel: "verloren_schliessen",
      gruppe: "kunde_ist_dran",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      aktion: aktionBauen("verloren_schliessen", fakten),
      dringlichkeit: "normal",
      bausteine: bau.bausteine,
    };
  }

  /**
   * Zeilen 7 und 9: der Fall ist das Ausbleiben der Antwort — sobald es einen vollen Werktag gibt,
   * trägt es den Kopf. Am Versandtag selbst gibt es diese Tatsache noch nicht; dann ist der
   * Versand der Fall, und ein „Keine Antwort seit …" wäre eine Behauptung ohne Grundlage.
   */
  const ohneAntwort = ohneAntwortTeil(angebot.versendetAm, u);
  bau.add(ohneAntwort, ["angebot.versendetAm"]);
  bau.add(versendetTeil(fakten, u), ["angebot.versendetAm"]);

  // Einblicke (730): „N× angesehen“ meint das AKTUELLE Angebot (seit dem letzten Versand).
  const aufrufeAktuell = angebot.aufrufeSeitVersand !== undefined ? angebot.aufrufeSeitVersand : angebot.aufrufe;
  if (istBelegt(aufrufeAktuell)) {
    bau.add(
      `${malWort(aufrufeAktuell as number)} angesehen`,
      [angebot.aufrufeSeitVersand !== undefined ? "angebot.aufrufeSeitVersand" : "angebot.aufrufe"],
      "komma",
    );
    if (istBelegt(angebot.zuletztAngesehenAm)) {
      const datum = datumKurz(angebot.zuletztAngesehenAm as Date, u.jetzt, u.zeitzone);
      bau.add(datum ? `zuletzt am ${datum}` : null, ["angebot.zuletztAngesehenAm"], "strich");
    }
  } else if (nieGeoeffnet) {
    bau.addFehlt("nie geöffnet", ["angebot.zuletztAngesehenAm"], "komma");
  }

  bau.add(nachgefasstTeil(fakten), ["angebot.nachfassungen"]);

  // Zeile 7: ab drei Werktagen ohne Antwort steht das Nachfassen an.
  if (werktage >= NACHFASS_WERKTAGE) {
    return {
      regel: "nachfassen",
      gruppe: "kunde_ist_dran",
      satz: bau.satz,
      kopf: bau.kopf,
      rest: bau.rest,
      aktion: aktionBauen("nachfassen", fakten),
      dringlichkeit: "normal",
      bausteine: bau.bausteine,
    };
  }

  // Zeile 9: es wartet — und zwar zu Recht. Keine Aktion, weil es nichts zu tun gibt.
  return {
    regel: "wartet",
    gruppe: "kunde_ist_dran",
    satz: bau.satz,
    kopf: bau.kopf,
    rest: bau.rest,
    aktion: null,
    dringlichkeit: "normal",
    bausteine: bau.bausteine,
  };
}
